// Hashing — Basic vs Optimized code for every "Beginner Problems" drill listed in the
// Hashing Explanation modal. Same shape as arraysDrills.js: concise steps + an interactive
// "watch it happen" visualizer per version, not just a paragraph and a code block.

const hashingDrills = [
  {
    id: 'valid-anagram',
    title: 'Check if Two Strings Are Anagrams',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Sort Both Strings and Compare',
        explanation: 'If two strings are anagrams, sorting both should produce identical results. Easy to reason about, but pays O(n log n) to answer a question that doesn\'t actually need full ordering.',
        steps: [
          'If the lengths differ, they can\'t be anagrams — return false immediately.',
          'Convert both strings to char arrays and sort each one.',
          'Compare the two sorted arrays element by element.',
          'If every position matches, the strings are anagrams.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['l', 'i', 's', 't', 'e', 'n'] }, { label: 't', values: ['s', 'i', 'l', 'e', 'n', 't'] }],
          varNames: [],
          steps: [
            { highlights: {}, note: 'Original: s = "listen", t = "silent". Both length 6 — lengths match, continue.' },
            { highlights: { s: [0, 1, 2, 3, 4, 5], t: [0, 1, 2, 3, 4, 5] }, snapshots: { s: ['e', 'i', 'l', 'n', 's', 't'], t: ['e', 'i', 'l', 'n', 's', 't'] }, note: 'After sorting both char arrays: "eilnst" and "eilnst" — identical → anagrams!' },
          ],
        },
        code:
`public boolean isAnagramSorting(String s, String t) {
    if (s.length() != t.length()) return false;
    char[] sChars = s.toCharArray();
    char[] tChars = t.toCharArray();
    Arrays.sort(sChars);
    Arrays.sort(tChars);
    return Arrays.equals(sChars, tChars);
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Frequency Count, Then Drain to Zero',
        explanation: 'Count every character in s, then walk t subtracting one count per character. If everything cancels out to zero, the strings share exactly the same characters in the same amounts.',
        steps: [
          'Build a frequency map by adding +1 for every character in s.',
          'Walk through t, subtracting 1 for every character seen.',
          'If a character in t was never in the map (or its count is already 0), they aren\'t anagrams.',
          'If every count drains to exactly zero, the strings are anagrams.',
        ],
        visual: {
          arrays: [
            { label: 's', values: ['l', 'i', 's', 't', 'e', 'n'] },
            { label: 't', values: ['s', 'i', 'l', 'e', 'n', 't'] },
            { label: 'freq[l,i,s,t,e,n]', values: [0, 0, 0, 0, 0, 0] },
          ],
          varNames: [],
          steps: [
            { highlights: { s: [0] }, snapshots: { 'freq[l,i,s,t,e,n]': [1, 0, 0, 0, 0, 0] }, note: "s[0]='l' → freq[l] = 1.", phase: 'Count s' },
            { highlights: { s: [1] }, snapshots: { 'freq[l,i,s,t,e,n]': [1, 1, 0, 0, 0, 0] }, note: "s[1]='i' → freq[i] = 1.", phase: 'Count s' },
            { highlights: { s: [2] }, snapshots: { 'freq[l,i,s,t,e,n]': [1, 1, 1, 0, 0, 0] }, note: "s[2]='s' → freq[s] = 1.", phase: 'Count s' },
            { highlights: { s: [3] }, snapshots: { 'freq[l,i,s,t,e,n]': [1, 1, 1, 1, 0, 0] }, note: "s[3]='t' → freq[t] = 1.", phase: 'Count s' },
            { highlights: { s: [4] }, snapshots: { 'freq[l,i,s,t,e,n]': [1, 1, 1, 1, 1, 0] }, note: "s[4]='e' → freq[e] = 1.", phase: 'Count s' },
            { highlights: { s: [5] }, snapshots: { 'freq[l,i,s,t,e,n]': [1, 1, 1, 1, 1, 1] }, note: "s[5]='n' → freq[n] = 1. Counting s done.", phase: 'Count s' },
            { highlights: { t: [0] }, snapshots: { 'freq[l,i,s,t,e,n]': [1, 1, 0, 1, 1, 1] }, note: "t[0]='s' → freq[s] drops to 0.", phase: 'Drain t' },
            { highlights: { t: [1] }, snapshots: { 'freq[l,i,s,t,e,n]': [1, 0, 0, 1, 1, 1] }, note: "t[1]='i' → freq[i] drops to 0.", phase: 'Drain t' },
            { highlights: { t: [2] }, snapshots: { 'freq[l,i,s,t,e,n]': [0, 0, 0, 1, 1, 1] }, note: "t[2]='l' → freq[l] drops to 0.", phase: 'Drain t' },
            { highlights: { t: [3] }, snapshots: { 'freq[l,i,s,t,e,n]': [0, 0, 0, 1, 0, 1] }, note: "t[3]='e' → freq[e] drops to 0.", phase: 'Drain t' },
            { highlights: { t: [4] }, snapshots: { 'freq[l,i,s,t,e,n]': [0, 0, 0, 1, 0, 0] }, note: "t[4]='n' → freq[n] drops to 0.", phase: 'Drain t' },
            { highlights: { t: [5] }, snapshots: { 'freq[l,i,s,t,e,n]': [0, 0, 0, 0, 0, 0] }, note: "t[5]='t' → freq[t] drops to 0. Every count is zero — anagrams confirmed!", phase: 'Drain t' },
          ],
        },
        code:
`public boolean isAnagramFrequency(String s, String t) {
    if (s.length() != t.length()) return false;

    Map<Character, Integer> freq = new HashMap<>();
    for (char c : s.toCharArray()) {
        freq.put(c, freq.getOrDefault(c, 0) + 1);
    }
    for (char c : t.toCharArray()) {
        if (!freq.containsKey(c) || freq.get(c) == 0) return false;
        freq.put(c, freq.get(c) - 1);
    }
    return true;
}`,
        complexity: { time: 'O(n)', space: 'O(1) — bounded by alphabet size' },
      },
    ],
  },
  {
    id: 'first-non-repeating-char',
    title: 'Find the First Non-Repeating Character',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Rescan the String for Each Character',
        explanation: 'For every character, count its total occurrences by scanning the whole string again. Correct, but every character triggers its own full scan.',
        steps: [
          'For each character, scan the entire string counting how many times it appears.',
          'If the count is exactly 1, that character is non-repeating — return it immediately.',
          'If nothing qualifies, return a sentinel value.',
          'Every character triggers its own O(n) scan — O(n²) total.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['s', 'w', 'i', 's', 's'] }],
          varNames: [],
          steps: [
            { highlights: { s: { primary: [0] } }, note: "Count how many times s[0]='s' appears in the whole string: 3 times. Not non-repeating." },
            { highlights: { s: { primary: [1] } }, note: "Count how many times s[1]='w' appears: 1 time. Non-repeating! Return 'w'." },
          ],
        },
        code:
`public char firstNonRepeatingBruteForce(String s) {
    for (int i = 0; i < s.length(); i++) {
        int count = 0;
        for (int j = 0; j < s.length(); j++) {
            if (s.charAt(j) == s.charAt(i)) count++;
        }
        if (count == 1) return s.charAt(i);
    }
    return '\\0';
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Frequency Map, Then One More Pass',
        explanation: 'Count every character once, then walk the string again looking for the first character whose count is 1. Two linear passes instead of one squared one.',
        steps: [
          'Build a frequency map for every character in one pass.',
          'Walk the string again from the start.',
          'Return the first character whose count in the map is exactly 1.',
          'Two O(n) passes total, instead of O(n²).',
        ],
        visual: {
          arrays: [
            { label: 's', values: ['s', 'w', 'i', 's', 's'] },
            { label: 'freq[s,w,i]', values: [0, 0, 0] },
          ],
          varNames: [],
          steps: [
            { highlights: { s: [0] }, snapshots: { 'freq[s,w,i]': [1, 0, 0] }, note: "s[0]='s' → freq[s] = 1.", phase: 'Count' },
            { highlights: { s: [1] }, snapshots: { 'freq[s,w,i]': [1, 1, 0] }, note: "s[1]='w' → freq[w] = 1.", phase: 'Count' },
            { highlights: { s: [2] }, snapshots: { 'freq[s,w,i]': [1, 1, 1] }, note: "s[2]='i' → freq[i] = 1.", phase: 'Count' },
            { highlights: { s: [3] }, snapshots: { 'freq[s,w,i]': [2, 1, 1] }, note: "s[3]='s' → freq[s] = 2.", phase: 'Count' },
            { highlights: { s: [4] }, snapshots: { 'freq[s,w,i]': [3, 1, 1] }, note: "s[4]='s' → freq[s] = 3. Counting done.", phase: 'Count' },
            { highlights: { s: [0] }, note: "Check s[0]='s': freq[s] = 3, not 1 — keep scanning.", phase: 'Scan' },
            { highlights: { s: [1] }, note: "Check s[1]='w': freq[w] = 1 — found! Return 'w'.", phase: 'Scan' },
          ],
        },
        code:
`public char firstNonRepeating(String s) {
    Map<Character, Integer> freq = new HashMap<>();
    for (char c : s.toCharArray()) {
        freq.put(c, freq.getOrDefault(c, 0) + 1);
    }
    for (char c : s.toCharArray()) {
        if (freq.get(c) == 1) return c;
    }
    return '\\0';
}`,
        complexity: { time: 'O(n)', space: 'O(1) — bounded by alphabet size' },
      },
    ],
  },
  {
    id: 'count-frequency-elements',
    title: 'Count the Frequency of Each Element in an Array',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Rescan the Array for Each Element',
        explanation: 'For every element, count its occurrences by scanning the whole array again, storing the result keyed by that value. Correct, but re-processes the same value repeatedly.',
        steps: [
          'For each element, scan the entire array counting matches.',
          'Store the result in a map keyed by that element\'s value.',
          'Re-processing a value already counted just overwrites the same count — harmless but wasteful.',
          'Every element triggers a full array scan — O(n²).',
        ],
        visual: {
          arrays: [
            { label: 'arr', values: [4, 2, 4, 5, 2, 4] },
            { label: 'freq[4,2,5]', values: [0, 0, 0] },
          ],
          varNames: [],
          steps: [
            { highlights: { arr: [0] }, snapshots: { 'freq[4,2,5]': [3, 0, 0] }, note: 'Scan the whole array for 4s: found 3. freq[4] = 3.' },
            { highlights: { arr: [1] }, snapshots: { 'freq[4,2,5]': [3, 2, 0] }, note: 'Scan the whole array for 2s: found 2. freq[2] = 2.' },
            { highlights: { arr: [2] }, snapshots: { 'freq[4,2,5]': [3, 2, 0] }, note: 'arr[2]=4 again — rescans and recomputes the same count (3). Wasted work.' },
            { highlights: { arr: [3] }, snapshots: { 'freq[4,2,5]': [3, 2, 1] }, note: 'Scan the whole array for 5s: found 1. freq[5] = 1.' },
            { highlights: { arr: [4] }, snapshots: { 'freq[4,2,5]': [3, 2, 1] }, note: 'arr[4]=2 again — recomputes the same count (2). Wasted work.' },
            { highlights: { arr: [5] }, snapshots: { 'freq[4,2,5]': [3, 2, 1] }, note: 'arr[5]=4 again — recomputes (3) one more time. Final: {4:3, 2:2, 5:1}.' },
          ],
        },
        code:
`public Map<Integer, Integer> countFrequencyBruteForce(int[] arr) {
    Map<Integer, Integer> freq = new HashMap<>();
    for (int i = 0; i < arr.length; i++) {
        int count = 0;
        for (int j = 0; j < arr.length; j++) {
            if (arr[j] == arr[i]) count++;
        }
        freq.put(arr[i], count);
    }
    return freq;
}`,
        complexity: { time: 'O(n²)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Single Pass Frequency Map',
        explanation: 'Walk the array once, incrementing each element\'s count with getOrDefault. No rescanning, no redundant work.',
        steps: [
          'Start with an empty frequency map.',
          'Walk the array once.',
          'For each element, increment its count with getOrDefault.',
          'One pass total — no rescanning.',
        ],
        visual: {
          arrays: [
            { label: 'arr', values: [4, 2, 4, 5, 2, 4] },
            { label: 'freq[4,2,5]', values: [0, 0, 0] },
          ],
          varNames: [],
          steps: [
            { highlights: { arr: [0] }, snapshots: { 'freq[4,2,5]': [1, 0, 0] }, note: 'arr[0]=4 → freq[4] becomes 1.' },
            { highlights: { arr: [1] }, snapshots: { 'freq[4,2,5]': [1, 1, 0] }, note: 'arr[1]=2 → freq[2] becomes 1.' },
            { highlights: { arr: [2] }, snapshots: { 'freq[4,2,5]': [2, 1, 0] }, note: 'arr[2]=4 → freq[4] becomes 2.' },
            { highlights: { arr: [3] }, snapshots: { 'freq[4,2,5]': [2, 1, 1] }, note: 'arr[3]=5 → freq[5] becomes 1.' },
            { highlights: { arr: [4] }, snapshots: { 'freq[4,2,5]': [2, 2, 1] }, note: 'arr[4]=2 → freq[2] becomes 2.' },
            { highlights: { arr: [5] }, snapshots: { 'freq[4,2,5]': [3, 2, 1] }, note: 'arr[5]=4 → freq[4] becomes 3. Final: {4:3, 2:2, 5:1} — one pass.' },
          ],
        },
        code:
`public Map<Integer, Integer> countFrequency(int[] arr) {
    Map<Integer, Integer> freq = new HashMap<>();
    for (int num : arr) {
        freq.put(num, freq.getOrDefault(num, 0) + 1);
    }
    return freq;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'two-arrays-equal-ignoring-order',
    title: 'Check if Two Arrays Contain the Same Elements (Ignoring Order)',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Sort Both and Compare',
        explanation: 'If two arrays contain the same elements, sorting both should make them identical. Simple, but pays O(n log n) for a question that\'s really about counting, not ordering.',
        steps: [
          'If the lengths differ, the arrays can\'t be equal — return false.',
          'Sort a copy of each array (so the originals stay untouched).',
          'Compare the sorted arrays element by element.',
          'If every position matches, the arrays contain the same elements.',
        ],
        visual: {
          arrays: [{ label: 'a', values: [1, 2, 2, 3] }, { label: 'b', values: [3, 1, 2, 2] }],
          varNames: [],
          steps: [
            { highlights: {}, note: 'Original: a = [1,2,2,3], b = [3,1,2,2]. Same length (4) — continue.' },
            { highlights: { a: [0, 1, 2, 3], b: [0, 1, 2, 3] }, snapshots: { a: [1, 2, 2, 3], b: [1, 2, 2, 3] }, note: 'After sorting both: [1,2,2,3] == [1,2,2,3] → equal!' },
          ],
        },
        code:
`public boolean areEqualSorting(int[] a, int[] b) {
    if (a.length != b.length) return false;
    int[] sortedA = Arrays.copyOf(a, a.length);
    int[] sortedB = Arrays.copyOf(b, b.length);
    Arrays.sort(sortedA);
    Arrays.sort(sortedB);
    return Arrays.equals(sortedA, sortedB);
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Frequency Map, Then Drain to Zero',
        explanation: 'Count every element of a, then walk b subtracting one count per element. If everything cancels to zero, both arrays hold exactly the same multiset of values.',
        steps: [
          'Build a frequency map by adding +1 for every element in a.',
          'Walk through b, subtracting 1 for every element seen.',
          'If a value in b isn\'t in the map (or its count is already 0), the arrays differ.',
          'If everything drains to zero, the arrays contain the same elements.',
        ],
        visual: {
          arrays: [
            { label: 'a', values: [1, 2, 2, 3] },
            { label: 'b', values: [3, 1, 2, 2] },
            { label: 'freq[1,2,3]', values: [0, 0, 0] },
          ],
          varNames: [],
          steps: [
            { highlights: { a: [0] }, snapshots: { 'freq[1,2,3]': [1, 0, 0] }, note: 'a[0]=1 → freq[1] = 1.', phase: 'Count a' },
            { highlights: { a: [1] }, snapshots: { 'freq[1,2,3]': [1, 1, 0] }, note: 'a[1]=2 → freq[2] = 1.', phase: 'Count a' },
            { highlights: { a: [2] }, snapshots: { 'freq[1,2,3]': [1, 2, 0] }, note: 'a[2]=2 → freq[2] = 2.', phase: 'Count a' },
            { highlights: { a: [3] }, snapshots: { 'freq[1,2,3]': [1, 2, 1] }, note: 'a[3]=3 → freq[3] = 1. Counting a done.', phase: 'Count a' },
            { highlights: { b: [0] }, snapshots: { 'freq[1,2,3]': [1, 2, 0] }, note: 'b[0]=3 → freq[3] drops to 0.', phase: 'Drain b' },
            { highlights: { b: [1] }, snapshots: { 'freq[1,2,3]': [0, 2, 0] }, note: 'b[1]=1 → freq[1] drops to 0.', phase: 'Drain b' },
            { highlights: { b: [2] }, snapshots: { 'freq[1,2,3]': [0, 1, 0] }, note: 'b[2]=2 → freq[2] drops to 1.', phase: 'Drain b' },
            { highlights: { b: [3] }, snapshots: { 'freq[1,2,3]': [0, 0, 0] }, note: 'b[3]=2 → freq[2] drops to 0. Everything is zero — equal confirmed!', phase: 'Drain b' },
          ],
        },
        code:
`public boolean areEqual(int[] a, int[] b) {
    if (a.length != b.length) return false;

    Map<Integer, Integer> freq = new HashMap<>();
    for (int num : a) {
        freq.put(num, freq.getOrDefault(num, 0) + 1);
    }
    for (int num : b) {
        if (!freq.containsKey(num) || freq.get(num) == 0) return false;
        freq.put(num, freq.get(num) - 1);
    }
    return true;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'longest-consecutive-sequence',
    title: 'Longest Consecutive Sequence',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Sort, Then Scan for Runs',
        explanation: 'Sorting brings consecutive numbers next to each other, so a single scan afterward can track run lengths. Simple, but pays O(n log n) up front.',
        steps: [
          'Sort the array first.',
          'Walk the sorted array, tracking the current run length.',
          'If the next value equals current+1, extend the run; if it\'s a duplicate, skip it; otherwise reset the run to 1.',
          'Track the longest run seen throughout the scan.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [100, 4, 200, 1, 3, 2] }],
          varNames: ['current', 'longest'],
          steps: [
            { highlights: {}, note: 'Original: [100, 4, 200, 1, 3, 2].' },
            { snapshots: { nums: [1, 2, 3, 4, 100, 200] }, note: 'After sorting: [1, 2, 3, 4, 100, 200].' },
            { highlights: { nums: [1] }, vars: { current: 2, longest: 2 }, note: '2 = 1+1 → extend. current = 2.' },
            { highlights: { nums: [2] }, vars: { current: 3, longest: 3 }, note: '3 = 2+1 → extend. current = 3.' },
            { highlights: { nums: [3] }, vars: { current: 4, longest: 4 }, note: '4 = 3+1 → extend. current = 4.' },
            { highlights: { nums: [4] }, vars: { current: 1, longest: 4 }, note: '100 ≠ 4+1 → reset. current = 1.' },
            { highlights: { nums: [5] }, vars: { current: 1, longest: 4 }, note: '200 ≠ 100+1 → reset. current = 1. Final longest: 4.' },
          ],
        },
        code:
`public int longestConsecutiveSorting(int[] nums) {
    if (nums.length == 0) return 0;
    int[] sorted = Arrays.copyOf(nums, nums.length);
    Arrays.sort(sorted);

    int longest = 1, current = 1;
    for (int i = 1; i < sorted.length; i++) {
        if (sorted[i] == sorted[i - 1]) continue; // skip duplicates
        if (sorted[i] == sorted[i - 1] + 1) {
            current++;
        } else {
            current = 1;
        }
        longest = Math.max(longest, current);
    }
    return longest;
}`,
        complexity: { time: 'O(n log n)', space: 'O(n)' },
      },
      {
        name: 'Optimized — HashSet, Only Count From Sequence Starts',
        explanation: 'Put everything in a HashSet, then only start counting forward from numbers that are the START of a sequence (num−1 not in the set). Every number ever gets counted as part of exactly one sequence\'s forward walk.',
        steps: [
          'Put every number into a HashSet for O(1) lookups.',
          'For each number, check if num−1 exists — if it does, this number is NOT a sequence start, skip it.',
          'If num−1 doesn\'t exist, count forward (num+1, num+2, …) while each exists in the set.',
          'Track the longest run found — no number is ever re-walked from two different starting points.',
        ],
        visual: {
          arrays: [{ label: 'nums', values: [100, 4, 200, 1, 3, 2] }],
          varNames: ['longest'],
          steps: [
            { highlights: { nums: [0] }, vars: { longest: 1 }, note: '100: is 99 in the set? No → start. Count forward: 101 not in set. Run length 1.' },
            { highlights: { nums: [1] }, vars: { longest: 1 }, note: '4: is 3 in the set? Yes → not a start, skip entirely (no wasted counting).' },
            { highlights: { nums: [2] }, vars: { longest: 1 }, note: '200: is 199 in the set? No → start. Count forward: 201 not in set. Run length 1.' },
            { highlights: { nums: [3] }, vars: { longest: 4 }, note: '1: is 0 in the set? No → start! Count forward: 2, 3, 4 all present, 5 is not. Run length 4. New longest: 4.' },
            { highlights: { nums: [4] }, vars: { longest: 4 }, note: '3: is 2 in the set? Yes → skip.' },
            { highlights: { nums: [5] }, vars: { longest: 4 }, note: '2: is 1 in the set? Yes → skip. Every number checked exactly once — final longest: 4.' },
          ],
        },
        code:
`public int longestConsecutive(int[] nums) {
    Set<Integer> numSet = new HashSet<>();
    for (int num : nums) numSet.add(num);

    int longest = 0;
    for (int num : numSet) {
        if (!numSet.contains(num - 1)) {   // only start counting from sequence starts
            int length = 1;
            while (numSet.contains(num + length)) {
                length++;
            }
            longest = Math.max(longest, length);
        }
    }
    return longest;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'subarray-sum-equals-k',
    title: 'Count Subarrays With Sum Equal to k',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Sum Every Subarray',
        explanation: 'Try every possible subarray, sum it, and check against k. Direct, but every subarray gets its own summation.',
        steps: [
          'For each start index i, reset a running sum.',
          'Extend the running sum to every end index j ≥ i.',
          'Whenever the running sum equals k, increment the count.',
          'Every subarray is summed once — O(n²) total.',
        ],
        visual: {
          arrays: [{ label: 'nums (k = 2)', values: [1, 1, 1] }],
          varNames: ['count'],
          steps: [
            { highlights: { nums: { primary: [0], secondary: [0] } }, vars: { count: 0 }, note: 'i=0, j=0: sum = 1. Not k.' },
            { highlights: { nums: { primary: [0], secondary: [1] } }, vars: { count: 1 }, note: 'i=0, j=1: sum = 1+1 = 2. Matches k! count = 1.' },
            { highlights: { nums: { primary: [0], secondary: [2] } }, vars: { count: 1 }, note: 'i=0, j=2: sum = 1+1+1 = 3. Not k.' },
            { highlights: { nums: { primary: [1], secondary: [1] } }, vars: { count: 1 }, note: 'i=1, j=1: sum = 1. Not k.' },
            { highlights: { nums: { primary: [1], secondary: [2] } }, vars: { count: 2 }, note: 'i=1, j=2: sum = 1+1 = 2. Matches! count = 2.' },
            { highlights: { nums: { primary: [2], secondary: [2] } }, vars: { count: 2 }, note: 'i=2, j=2: sum = 1. Not k. All 6 subarrays checked. Final count: 2.' },
          ],
        },
        code:
`public int subarraySumBruteForce(int[] nums, int k) {
    int count = 0;
    for (int i = 0; i < nums.length; i++) {
        int sum = 0;
        for (int j = i; j < nums.length; j++) {
            sum += nums[j];
            if (sum == k) count++;
        }
    }
    return count;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Prefix Sum + HashMap of Counts',
        explanation: 'Track a running prefix sum and, at every step, ask "have I seen a prefix sum of (current − k) before?" Every time you have, that many subarrays ending here sum to exactly k.',
        steps: [
          'Keep a running prefix sum as you scan left to right.',
          'Maintain a map of how many times each prefix sum value has occurred — start with {0: 1} for the empty prefix.',
          'At each step, add prefixCount.get(sum − k) to the answer, if present.',
          'Then record the current sum in the map before moving to the next element.',
        ],
        visual: {
          arrays: [{ label: 'nums (k = 2)', values: [1, 1, 1] }],
          varNames: ['sum', 'count'],
          steps: [
            { highlights: { nums: [0] }, vars: { sum: 1, count: 0 }, note: 'num=1 → sum=1. Look for sum−k=−1 in prefixCount: not found. count stays 0. Record prefixCount[1]=1.' },
            { highlights: { nums: [1] }, vars: { sum: 2, count: 1 }, note: 'num=1 → sum=2. Look for sum−k=0: found (the empty prefix, seen once)! count becomes 1. Record prefixCount[2]=1.' },
            { highlights: { nums: [2] }, vars: { sum: 3, count: 2 }, note: 'num=1 → sum=3. Look for sum−k=1: found (seen once)! count becomes 2. Final answer: 2.' },
          ],
        },
        code:
`public int subarraySum(int[] nums, int k) {
    Map<Integer, Integer> prefixCount = new HashMap<>();
    prefixCount.put(0, 1); // empty prefix
    int sum = 0, count = 0;

    for (int num : nums) {
        sum += num;
        count += prefixCount.getOrDefault(sum - k, 0);
        prefixCount.put(sum, prefixCount.getOrDefault(sum, 0) + 1);
    }
    return count;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'pairs-with-given-sum',
    title: 'Find All Pairs in an Array With a Given Sum',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Check Every Pair',
        explanation: 'Compare every element to every later element. Direct and correct, but O(n²) pairs checked no matter how few matches exist.',
        steps: [
          'For each element, check every later element for a matching sum.',
          'If arr[i] + arr[j] equals target, record the pair.',
          'Keep checking all remaining pairs even after a match — no early exit.',
          'O(n²) pairs total.',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 9)', values: [2, 4, 3, 5, 7, 8] }],
          varNames: [],
          steps: [
            { highlights: { arr: { primary: [0], secondary: [1] } }, note: '2+4=6. No.' },
            { highlights: { arr: { primary: [0], secondary: [2] } }, note: '2+3=5. No.' },
            { highlights: { arr: { primary: [0], secondary: [3] } }, note: '2+5=7. No.' },
            { highlights: { arr: { primary: [0], secondary: [4] } }, note: '2+7=9. MATCH — pair (2, 7).' },
            { highlights: { arr: { primary: [0], secondary: [5] } }, note: '2+8=10. No.' },
            { highlights: { arr: { primary: [1], secondary: [2] } }, note: '4+3=7. No.' },
            { highlights: { arr: { primary: [1], secondary: [3] } }, note: '4+5=9. MATCH — pair (4, 5).' },
            { highlights: { arr: { primary: [1], secondary: [4] } }, note: '4+7=11. No.' },
            { highlights: { arr: { primary: [1], secondary: [5] } }, note: '4+8=12. No.' },
            { highlights: { arr: { primary: [2], secondary: [3] } }, note: '3+5=8. No.' },
            { highlights: { arr: { primary: [2], secondary: [4] } }, note: '3+7=10. No.' },
            { highlights: { arr: { primary: [2], secondary: [5] } }, note: '3+8=11. No.' },
            { highlights: { arr: { primary: [3], secondary: [4] } }, note: '5+7=12. No.' },
            { highlights: { arr: { primary: [3], secondary: [5] } }, note: '5+8=13. No.' },
            { highlights: { arr: { primary: [4], secondary: [5] } }, note: '7+8=15. No. All 15 pairs checked. Final pairs: (2,7) and (4,5).' },
          ],
        },
        code:
`public List<int[]> findPairsBruteForce(int[] arr, int target) {
    List<int[]> pairs = new ArrayList<>();
    for (int i = 0; i < arr.length; i++) {
        for (int j = i + 1; j < arr.length; j++) {
            if (arr[i] + arr[j] == target) {
                pairs.add(new int[] { arr[i], arr[j] });
            }
        }
    }
    return pairs;
}`,
        complexity: { time: 'O(n²)', space: 'O(1) extra' },
      },
      {
        name: 'Optimized — HashSet Complement Lookup',
        explanation: 'Keep a set of numbers seen so far. For each new number, its complement either is or isn\'t already in the set — no need to compare against every other element individually.',
        steps: [
          'Keep a HashSet of numbers seen so far.',
          'For each element, compute complement = target − current.',
          'If the complement is already in the set, record the pair.',
          'Add the current element to the set and move on — one pass.',
        ],
        visual: {
          arrays: [{ label: 'arr (target = 9)', values: [2, 4, 3, 5, 7, 8] }],
          varNames: [],
          steps: [
            { highlights: { arr: [0] }, note: '2: complement = 7. Not in set yet. Add 2. seen = {2}.' },
            { highlights: { arr: [1] }, note: '4: complement = 5. Not in set. Add 4. seen = {2, 4}.' },
            { highlights: { arr: [2] }, note: '3: complement = 6. Not in set. Add 3. seen = {2, 4, 3}.' },
            { highlights: { arr: [3] }, note: '5: complement = 4. Found in set! Pair: (4, 5). Add 5. seen = {2, 4, 3, 5}.' },
            { highlights: { arr: [4] }, note: '7: complement = 2. Found in set! Pair: (2, 7). Add 7. seen = {2, 4, 3, 5, 7}.' },
            { highlights: { arr: [5] }, note: '8: complement = 1. Not in set. Final pairs: (4,5) and (2,7) — one pass, no nested loop.' },
          ],
        },
        code:
`public List<int[]> findPairs(int[] arr, int target) {
    List<int[]> pairs = new ArrayList<>();
    Set<Integer> seen = new HashSet<>();
    for (int num : arr) {
        int complement = target - num;
        if (seen.contains(complement)) {
            pairs.add(new int[] { complement, num });
        }
        seen.add(num);
    }
    return pairs;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'longest-substring-no-repeat',
    title: 'Longest Substring Without Repeating Characters',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Check Every Substring',
        explanation: 'For each starting point, grow a substring one character at a time using a Set to detect the first repeat. Correct, but overlapping substrings get re-checked from scratch.',
        steps: [
          'For each starting index, grow a substring one character at a time.',
          'Use a Set to check whether the growing substring already contains that character.',
          'The moment a repeat appears, stop growing from this start.',
          'Track the longest valid substring length — overlapping work happens for every start.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['a', 'b', 'c', 'a', 'b', 'c', 'b', 'b'] }],
          varNames: ['longest'],
          steps: [
            { highlights: { s: { primary: [0, 1, 2] } }, vars: { longest: 3 }, note: 'Start at 0: "abc" grows to length 3, then index 3 (\'a\') repeats — stop. Best: 3.' },
            { highlights: { s: { primary: [1, 2, 3] } }, vars: { longest: 3 }, note: 'Start at 1: "bca" grows to length 3, then \'b\' repeats — stop. Best: 3.' },
            { highlights: { s: { primary: [2, 3, 4] } }, vars: { longest: 3 }, note: 'Start at 2: "cab" grows to length 3, then \'c\' repeats — stop. Best: 3.' },
            { highlights: { s: { primary: [3, 4, 5] } }, vars: { longest: 3 }, note: 'Start at 3: "abc" grows to length 3, then \'b\' repeats — stop. Best: 3.' },
            { highlights: { s: { primary: [4, 5] } }, vars: { longest: 3 }, note: 'Start at 4: "bc" grows to length 2, then \'b\' repeats — stop. Best: 2, not better.' },
            { highlights: { s: { primary: [5, 6] } }, vars: { longest: 3 }, note: 'Start at 5: "cb" grows to length 2, then \'b\' repeats — stop. Best: 2.' },
            { highlights: { s: { primary: [6] } }, vars: { longest: 3 }, note: 'Start at 6: just "b", the next \'b\' repeats immediately — stop. Best: 1.' },
            { highlights: { s: { primary: [7] } }, vars: { longest: 3 }, note: 'Start at 7: just "b", end of string. Best: 1. Overall longest: 3.' },
          ],
        },
        code:
`public int longestSubstringBruteForce(String s) {
    int longest = 0;
    for (int i = 0; i < s.length(); i++) {
        Set<Character> seen = new HashSet<>();
        for (int j = i; j < s.length(); j++) {
            if (!seen.add(s.charAt(j))) break; // repeat found, stop growing
            longest = Math.max(longest, j - i + 1);
        }
    }
    return longest;
}`,
        complexity: { time: 'O(n²)', space: 'O(n)' },
      },
      {
        name: 'Optimized — Sliding Window + Last-Seen-Index Map',
        explanation: 'Keep a window [left, right] with no repeated characters, and a map of each character\'s most recent index. A repeat inside the window jumps left forward instead of restarting from scratch.',
        steps: [
          'Keep a HashMap of each character\'s most recent index.',
          'Move a right pointer forward one character at a time.',
          'If the current character was seen inside the current window, jump left forward past its last occurrence.',
          'Track the window size (right − left + 1) as the answer at every step.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['a', 'b', 'c', 'a', 'b', 'c', 'b', 'b'] }],
          varNames: ['left', 'longest'],
          steps: [
            { highlights: { s: { primary: [0] } }, vars: { left: 0, longest: 1 }, note: "right=0 ('a'): not seen before. Window [0,0], length 1." },
            { highlights: { s: { primary: [0, 1] } }, vars: { left: 0, longest: 2 }, note: "right=1 ('b'): not seen. Window [0,1], length 2." },
            { highlights: { s: { primary: [0, 1, 2] } }, vars: { left: 0, longest: 3 }, note: "right=2 ('c'): not seen. Window [0,2], length 3." },
            { highlights: { s: { primary: [1, 2, 3] } }, vars: { left: 1, longest: 3 }, note: "right=3 ('a'): last seen at 0, inside window → left jumps to 1. Window [1,3], length 3." },
            { highlights: { s: { primary: [2, 3, 4] } }, vars: { left: 2, longest: 3 }, note: "right=4 ('b'): last seen at 1, inside window → left jumps to 2. Window [2,4], length 3." },
            { highlights: { s: { primary: [3, 4, 5] } }, vars: { left: 3, longest: 3 }, note: "right=5 ('c'): last seen at 2, inside window → left jumps to 3. Window [3,5], length 3." },
            { highlights: { s: { primary: [5, 6] } }, vars: { left: 5, longest: 3 }, note: "right=6 ('b'): last seen at 4, inside window → left jumps to 5. Window [5,6], length 2." },
            { highlights: { s: { primary: [7] } }, vars: { left: 7, longest: 3 }, note: "right=7 ('b'): last seen at 6, inside window → left jumps to 7. Window [7,7], length 1. Final longest: 3." },
          ],
        },
        code:
`public int longestSubstring(String s) {
    Map<Character, Integer> lastSeen = new HashMap<>();
    int longest = 0, left = 0;

    for (int right = 0; right < s.length(); right++) {
        char c = s.charAt(right);
        if (lastSeen.containsKey(c) && lastSeen.get(c) >= left) {
            left = lastSeen.get(c) + 1;   // jump past the repeat
        }
        lastSeen.put(c, right);
        longest = Math.max(longest, right - left + 1);
    }
    return longest;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
  {
    id: 'isomorphic-strings',
    title: 'Check if Two Strings Are Isomorphic',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — Compare Character Position Patterns',
        explanation: 'Two strings are isomorphic if the PATTERN of repeated positions matches exactly. Check that every pair of positions agrees or disagrees the same way in both strings.',
        steps: [
          'For each pair of positions (i, j), check whether s\'s characters there are equal.',
          'Check whether t\'s characters at the same positions are equal too.',
          'If one pair matches but the other doesn\'t, the strings are not isomorphic.',
          'Repeating this for every position pair means rescanning relationships — O(n²).',
        ],
        visual: {
          arrays: [{ label: 's', values: ['e', 'g', 'g'] }, { label: 't', values: ['a', 'd', 'd'] }],
          varNames: [],
          steps: [
            { highlights: { s: { primary: [0], secondary: [1] }, t: { primary: [0], secondary: [1] } }, note: "s[0] vs s[1]: 'e' ≠ 'g'. t[0] vs t[1]: 'a' ≠ 'd'. Same relationship (different) — consistent so far." },
            { highlights: { s: { primary: [0], secondary: [2] }, t: { primary: [0], secondary: [2] } }, note: "s[0] vs s[2]: 'e' ≠ 'g'. t[0] vs t[2]: 'a' ≠ 'd'. Still consistent." },
            { highlights: { s: { primary: [1], secondary: [2] }, t: { primary: [1], secondary: [2] } }, note: "s[1] vs s[2]: 'g' = 'g'. t[1] vs t[2]: 'd' = 'd'. Pattern matches exactly — isomorphic!" },
          ],
        },
        code:
`public boolean isIsomorphicBruteForce(String s, String t) {
    if (s.length() != t.length()) return false;
    for (int i = 0; i < s.length(); i++) {
        for (int j = 0; j < s.length(); j++) {
            boolean sMatch = s.charAt(i) == s.charAt(j);
            boolean tMatch = t.charAt(i) == t.charAt(j);
            if (sMatch != tMatch) return false;
        }
    }
    return true;
}`,
        complexity: { time: 'O(n²)', space: 'O(1)' },
      },
      {
        name: 'Optimized — Two HashMaps for Bidirectional Mapping',
        explanation: 'Track the mapping in both directions at once (s→t and t→s). A conflict in either direction immediately disqualifies the strings — one pass, no repeated comparisons.',
        steps: [
          'Keep two maps: s-character → t-character, and t-character → s-character.',
          'At each position, check both directions for a conflicting mapping.',
          'If either direction conflicts with what\'s already recorded, return false.',
          'Otherwise record both mappings and continue — one pass.',
        ],
        visual: {
          arrays: [{ label: 's', values: ['e', 'g', 'g'] }, { label: 't', values: ['a', 'd', 'd'] }],
          varNames: [],
          steps: [
            { highlights: { s: [0], t: [0] }, note: "'e' → 'a': no conflict either direction. Record e→a, a→e." },
            { highlights: { s: [1], t: [1] }, note: "'g' → 'd': no conflict. Record g→d, d→g." },
            { highlights: { s: [2], t: [2] }, note: "'g' → 'd' again: sToT[g] is already 'd' — matches, no conflict. Isomorphic confirmed in one pass." },
          ],
        },
        code:
`public boolean isIsomorphic(String s, String t) {
    Map<Character, Character> sToT = new HashMap<>();
    Map<Character, Character> tToS = new HashMap<>();

    for (int i = 0; i < s.length(); i++) {
        char a = s.charAt(i), b = t.charAt(i);
        if (sToT.containsKey(a) && sToT.get(a) != b) return false;
        if (tToS.containsKey(b) && tToS.get(b) != a) return false;
        sToT.put(a, b);
        tToS.put(b, a);
    }
    return true;
}`,
        complexity: { time: 'O(n)', space: 'O(n)' },
      },
    ],
  },
];

export default hashingDrills;
